import { useState } from "react";
import { Megaphone, Send, ArrowRightLeft } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useMyAnnouncements,
  useAnnouncementFeed,
  usePublishAnnouncement,
  useWithdrawAnnouncement,
} from "@/hooks/useAnnouncements";
import type { Announcement } from "@/services/announcementService";
import { fmtMoney, statusBadgeVariant, EmptyState } from "./helpers";
import { ConvertToHandshakeDialog } from "./AnnouncementDialogs";



// ─── Announcements list ───────────────────────────────────────────

export function AnnouncementsList() {
  const [view, setView] = useState<"mine" | "feed">("feed");
  const { data: feedData, isLoading: feedLoading } = useAnnouncementFeed(
    undefined,
    { enabled: view === "feed" },
  );
  const { data: mine, isLoading: mineLoading } = useMyAnnouncements(undefined, {
    enabled: view === "mine",
  });

  const isLoading = view === "feed" ? feedLoading : mineLoading;
  const items: Announcement[] =
    view === "feed" ? (feedData ?? []) : (mine ?? []);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button
          variant={view === "feed" ? "default" : "outline"}
          size="sm"
          onClick={() => setView("feed")}
        >
          Vendor feed
        </Button>
        <Button
          variant={view === "mine" ? "default" : "outline"}
          size="sm"
          onClick={() => setView("mine")}
        >
          My announcements
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Megaphone className="h-10 w-10 text-muted-foreground/60" />}
          title={
            view === "feed"
              ? "No announcements from your vendors"
              : "You haven't published any announcements"
          }
          description={
            view === "feed"
              ? "Connected vendors' published offers and restocks will appear here."
              : "Broadcast stock availability or restock notifications to all connected distributors."
          }
        />
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <AnnouncementRow
              key={a._id}
              announcement={a}
              isMine={view === "mine"}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AnnouncementRow({
  announcement,
  isMine,
}: {
  announcement: Announcement;
  isMine: boolean;
}) {
  const [convertOpen, setConvertOpen] = useState(false);
  const withdraw = useWithdrawAnnouncement();
  const publish = usePublishAnnouncement();

  return (
    <>
      <Card className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="font-medium truncate">{announcement.title}</h3>
              <Badge variant={statusBadgeVariant(announcement.status)}>
                {announcement.status}
              </Badge>
              <Badge variant="outline" className="text-xs capitalize">
                {announcement.kind}
              </Badge>
            </div>
            {announcement.body && (
              <p className="text-sm text-muted-foreground line-clamp-2">
                {announcement.body}
              </p>
            )}
            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
              {announcement.unitPriceMinor != null && (
                <span>
                  Unit:{" "}
                  <strong className="font-medium">
                    {fmtMoney(
                      announcement.unitPriceMinor,
                      announcement.currency,
                    )}
                  </strong>
                </span>
              )}
              {announcement.availableQuantity != null && (
                <span>Available: {announcement.availableQuantity}</span>
              )}
              {announcement.expiresAt && (
                <span>
                  Expires{" "}
                  {formatDistanceToNow(new Date(announcement.expiresAt), {
                    addSuffix: true,
                  })}
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2 shrink-0">
            {!isMine && announcement.status === "published" && (
              <Button size="sm" onClick={() => setConvertOpen(true)}>
                <ArrowRightLeft className="h-3.5 w-3.5 mr-1.5" />
                Convert
              </Button>
            )}
            {isMine && announcement.status === "draft" && (
              <Button
                size="sm"
                onClick={() =>
                  publish.mutate(
                    { id: announcement._id },
                    {
                      onSuccess: () => toast.success("Announcement published"),
                      onError: (e: any) =>
                        toast.error(e?.response?.data?.message || "Failed"),
                    },
                  )
                }
              >
                <Send className="h-3.5 w-3.5 mr-1.5" />
                Publish
              </Button>
            )}
            {isMine && announcement.status === "published" && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (!confirm("Withdraw this announcement?")) return;
                  withdraw.mutate(
                    { id: announcement._id, reason: "Withdrawn by vendor" },
                    {
                      onSuccess: () => toast.success("Announcement withdrawn"),
                      onError: (e: any) =>
                        toast.error(e?.response?.data?.message || "Failed"),
                    },
                  );
                }}
              >
                Withdraw
              </Button>
            )}
          </div>
        </div>
      </Card>

      <ConvertToHandshakeDialog
        open={convertOpen}
        onClose={() => setConvertOpen(false)}
        announcement={announcement}
      />
    </>
  );
}



